import { EXTERNAL_ID_SESSION_STORAGE_KEY } from '@cof/refi-web-sdk/dist/utils/stratum';
import { DebugService } from '../debug/debug.service.js';
import { REFI_LOGIN_CONST } from '../../constants/login.constants.js';
import getDateFromString from '../../utils/get-date-from-string.js';
import { CODELESS_ENTRY_CONST, REFI_WEB_UI_CONST, EXPRESS_RETURN_CONST } from '../../constants/constants.js';
import { HostAppEventsService } from '../../utils/host-app-events-service.js';

class _SessionService {
  constructor() {
    this.debug = new DebugService('SessionService');
    this.sessionStorageKey = REFI_LOGIN_CONST.REFI_SESSION_KEY;
    this.offerStorageKey = 'auto-refi-offerkey';
    this.prequalStorageKey = 'refi-prequal-store';
  }

  get session() {
    this.debug.log('getting session');
    return JSON.parse(sessionStorage.getItem(this.sessionStorageKey));
  }

  setSession(eAppId, currentApp) {
    this.debug.log('setSession with', eAppId);
    sessionStorage.setItem(this.sessionStorageKey, JSON.stringify({ eAppId, currentApp }));
  }

  setAppReferenceId(appId) {
    sessionStorage.setItem(REFI_WEB_UI_CONST.appReferenceId, appId);
  }

  getAppReferenceId() {
    return sessionStorage.getItem(REFI_WEB_UI_CONST.appReferenceId);
  }

  getAttemptedStsBypass() {
    return sessionStorage.getItem(REFI_WEB_UI_CONST.attemptedStsBypass);
  }

  setAttemptedStsBypass(flag) {
    sessionStorage.setItem(REFI_WEB_UI_CONST.attemptedStsBypass, flag);
  }

  deleteAttemptedStsBypass() {
    sessionStorage.removeItem(REFI_WEB_UI_CONST.attemptedStsBypass);
  }

  setEaseGrStsRoutingFlag() {
    sessionStorage.setItem(REFI_WEB_UI_CONST.easeGrStsRoutingFlag, 'true');
  }

  getEaseGrStsRoutingFlag() {
    return sessionStorage.getItem(REFI_WEB_UI_CONST.easeGrStsRoutingFlag);
  }

  removeEaseGrStsRoutingFlag() {
    sessionStorage.removeItem(REFI_WEB_UI_CONST.easeGrStsRoutingFlag);
  }

  setEaseBauStsRoutingFlag() {
    sessionStorage.setItem(REFI_WEB_UI_CONST.easeBauStsRoutingFlag, 'true');
  }

  getEaseBauStsRoutingFlag() {
    return sessionStorage.getItem(REFI_WEB_UI_CONST.easeBauStsRoutingFlag);
  }

  removeEaseBauStsRoutingFlag() {
    sessionStorage.removeItem(REFI_WEB_UI_CONST.easeBauStsRoutingFlag);
  }

  setCwOfferStsRoutingFlag() {
    sessionStorage.setItem(REFI_WEB_UI_CONST.cwOfferStsRoutingFlag, 'true');
  }

  getCwOfferStsRoutingFlag() {
    return sessionStorage.getItem(REFI_WEB_UI_CONST.cwOfferStsRoutingFlag);
  }

  removeCwOfferStsRoutingFlag() {
    sessionStorage.removeItem(REFI_WEB_UI_CONST.cwOfferStsRoutingFlag);
  }

  setCwBauStsRoutingFlag() {
    sessionStorage.setItem(REFI_WEB_UI_CONST.cwBauStsRoutingFlag, 'true');
  }

  getCwBauStsRoutingFlag() {
    return sessionStorage.getItem(REFI_WEB_UI_CONST.cwBauStsRoutingFlag);
  }

  removeCwBauStsRoutingFlag() {
    sessionStorage.removeItem(REFI_WEB_UI_CONST.cwBauStsRoutingFlag);
  }

  setSsoCapturedState(key, value) {
    sessionStorage.setItem(key, JSON.stringify(value));
  }

  popSsoCapturedState(key) {
    const state = JSON.parse(sessionStorage.getItem(key));
    sessionStorage.removeItem(key);
    return state;
  }

  removeSession() {
    this.debug.log('removeSession');
    sessionStorage.removeItem(this.sessionStorageKey);
  }

  setSessionObject(app) {
    const { applicationId, applicationStatus, applicationStageIndicator, lineOfBusinessId, lineOfBusinessName, isPrequalifiedApp, expirationTimestamp, receivedTimestamp } = app;
    const encodedAppId = window.btoa(applicationId);
    const appStatusMap = {
      Received: 400,
      Returned: 409,
      Approved: 405,
      Pending: 408,
      Reviewing: 403,
      Verifying: 404,
      PaidPrefund: 411,
      Funding: 408,
      Funded: 407,
      Canceled: 401,
      Expired: 410,
      Declined: 402,
    };
    const sessionObj = {
      appId: applicationId,
      appReceivedDate: getDateFromString(receivedTimestamp),
      appStatusId: appStatusMap[applicationStatus],
      validityEndDate: expirationTimestamp,
      lobId: lineOfBusinessId,
      loanAppStatus: applicationStatus,
      loanLineOfBusiness: lineOfBusinessName,
      isPrequalifiedApp,
      statFlag: applicationStageIndicator,
      encryptedAppId: encodedAppId,
    };
    this.setSession(encodedAppId, sessionObj);
  }

  setAuthSession(appDetails) {
    sessionStorage.setItem(REFI_WEB_UI_CONST.sessionStorageKey, JSON.stringify(appDetails));
  }

  getAuthSession() {
    const appDetails = this.sessionParams();
    const showMfeContainer = !!appDetails;
    return { appDetails, showMfeContainer };
  }

  sessionParams() {
    const hostAppStorage = sessionStorage.getItem(REFI_WEB_UI_CONST.sessionStorageKey);
    if (hostAppStorage) {
      return JSON.parse(hostAppStorage);
    }
    return undefined;
  }

  clearSession() {
    sessionStorage.clear();
  }

  getUpfrontOffersSession() {
    return sessionStorage.getItem(REFI_WEB_UI_CONST.isUpfrontOffersKey);
  }

  setUpfrontOffersSession(value) {
    sessionStorage.setItem(REFI_WEB_UI_CONST.isUpfrontOffersKey, value);
  }

  getIconicV2Session() {
    return sessionStorage.getItem(REFI_WEB_UI_CONST.isIconicV2Key);
  }

  setIconicV2Session(value) {
    sessionStorage.setItem(REFI_WEB_UI_CONST.isIconicV2Key, value);
  }

  setMarketingSource(key) {
    sessionStorage.setItem(REFI_WEB_UI_CONST.marketingSourceStorageKey, key);
  }

  getMarketingSource() {
    return sessionStorage.getItem(REFI_WEB_UI_CONST.marketingSourceStorageKey);
  }

  setOpenApps(apps) {
    sessionStorage.setItem(REFI_WEB_UI_CONST.openAppIds, JSON.stringify(apps));
  }

  getOpenApps() {
    return sessionStorage.getItem(REFI_WEB_UI_CONST.openAppIds);
  }

  setHasCoApp(key) {
    sessionStorage.setItem(REFI_WEB_UI_CONST.hasCoApp, key);
  }

  getHasCoApp() {
    return sessionStorage.getItem(REFI_WEB_UI_CONST.hasCoApp);
  }

  setIsNewApp(key) {
    sessionStorage.setItem(REFI_WEB_UI_CONST.isNewAppKey, key);
  }

  getIsNewApp() {
    return sessionStorage.getItem(REFI_WEB_UI_CONST.isNewAppKey);
  }

  getCachedOffer() {
    const offerString = localStorage.getItem(this.offerStorageKey);
    if (!offerString) {
      return null;
    }
    let offer = JSON.parse(offerString);
    if (!offer.expirationDate) {
      return null;
    }
    if (!this.isOfferStillValid(new Date(offer.expirationDate))) {
      offer = null;
      this.removeCachedOffer();
      this.debug.log('offer expired');
    }
    return offer;
  }

  isOfferStillValid(expirationDate) {
    const currentdate = new Date();
    if (!expirationDate || expirationDate < currentdate) {
      return false;
    }
    return true;
  }

  setCachedOffer(resultingOffer) {
    const expirationDate = new Date();
    // add time to live
    expirationDate.setHours(expirationDate.getHours() + REFI_WEB_UI_CONST.OFFER_CODE_TIME_TO_LIVE_HOURS);
    resultingOffer.expirationDate = expirationDate;
    // TODO: decom this.offerStorageKey once we fully switch over to Host app v2
    localStorage.setItem(this.offerStorageKey, JSON.stringify(resultingOffer));
    sessionStorage.setItem(this.offerStorageKey, JSON.stringify(resultingOffer));
    this.prequalStoreCachedOffer(resultingOffer);
  }

  removeCachedOffer() {
    this.debug.log('removing access code offer');
    localStorage.removeItem(this.offerStorageKey);
    sessionStorage.removeItem(this.offerStorageKey);
    this.prequalStoreCachedOffer('');
  }

  prequalStoreCachedOffer(offerDetails) {
    this.debug.log('caching offer to prequal store, offer details: ', offerDetails);
    const prequalStore = JSON.parse(sessionStorage.getItem(this.prequalStorageKey)) || {};
    prequalStore.state = prequalStore.state || {};
    prequalStore.state.cachedOffer = offerDetails;
    sessionStorage.setItem(this.prequalStorageKey, JSON.stringify(prequalStore));
    HostAppEventsService.sendUpdatePrequalStoreEvent();
  }

  setPromoCode(promoCode) {
    this.prequalStoreCachedPromoCode(promoCode);
  }

  prequalStoreCachedPromoCode(promoCode) {
    this.debug.log('caching promocode to prequal store, promo code ', promoCode);
    const prequalStore = JSON.parse(sessionStorage.getItem(this.prequalStorageKey)) || {};
    prequalStore.state = prequalStore.state || {};
    prequalStore.state.promoCode = promoCode;
    sessionStorage.setItem(this.prequalStorageKey, JSON.stringify(prequalStore));
    HostAppEventsService.sendUpdatePrequalStoreEvent();
  }

  getExternalId() {
    return sessionStorage.getItem(EXTERNAL_ID_SESSION_STORAGE_KEY);
  }

  setExternalIdInSession(externalId) {
    this.debug.log(`setting external id ${externalId} in session storage`);
    sessionStorage.setItem(EXTERNAL_ID_SESSION_STORAGE_KEY, externalId);
  }

  getShowErrorMessageCount() {
    return sessionStorage.getItem(CODELESS_ENTRY_CONST.SESSION_STORAGE.ERROR_MESSAGE_COUNT_KEY);
  }

  setShowErrorMessageCount(count) {
    this.debug.log(`setting showErrorMessage count in session storage`);
    sessionStorage.setItem(CODELESS_ENTRY_CONST.SESSION_STORAGE.ERROR_MESSAGE_COUNT_KEY, count);
  }

  getProductType() {
    return sessionStorage.getItem(REFI_WEB_UI_CONST.PRODUCT_TYPE_SESSION_STORAGE_KEY);
  }

  setProductTypeInSession(productType) {
    this.debug.log(`setting product type ${productType} in session storage`);
    sessionStorage.setItem(REFI_WEB_UI_CONST.PRODUCT_TYPE_SESSION_STORAGE_KEY, productType);
  }

  getExperimentsOverride() {
    this.debug.log(`retrieving experiments override from session storage`);
    return JSON.parse(sessionStorage.getItem(REFI_WEB_UI_CONST.experimentsOverrideKey));
  }

  set clientCorrelationId(ccid) {
    this.debug.log(`Saving client correlation id ${ccid} into storage`);
    sessionStorage.setItem(REFI_WEB_UI_CONST.CLIENT_CORRELATION_ID_HEADER, ccid);
  }

  get clientCorrelationId() {
    const ccid = sessionStorage.getItem(REFI_WEB_UI_CONST.CLIENT_CORRELATION_ID_HEADER);
    if (ccid) {
      this.debug.log(`Retrieved client correlation id ${ccid} from session`);
      return ccid;
    }
    this.debug.log('No client correlation id in session');
    return ccid;
  }

  setExpressEntryReturnCustValue(returnCust) {
    this.debug.log(`setting express return return cust value ${returnCust} in session storage`);
    sessionStorage.setItem(EXPRESS_RETURN_CONST.EXPRESS_RETURN_CUSTOMER, returnCust);
  }

  /**
   * @function setDocUploadedFlag
   * @param {number} taskId - Task Id
   * @param {boolean} isSuccessful - if document was successfully uploaded
   * @return {void}
   */
  setDocUploadedFlag(taskId, isSuccessful) {
    const currentFlag = JSON.parse(sessionStorage.getItem(REFI_WEB_UI_CONST.DOC_UPLOADED_FLAG));
    const newFlag = {
      ...currentFlag,
      [taskId]: isSuccessful ? 'true' : 'false',
    };
    sessionStorage.setItem(REFI_WEB_UI_CONST.DOC_UPLOADED_FLAG, JSON.stringify(newFlag));
  }
}

const SessionService = new _SessionService();
export default SessionService;
